import React, { useState, useRef, useEffect } from 'react';

export const GaugeSection = ({ rating, onRatingSubmit, compact = false, icon = '', static: isStatic = false }) => {
    const [isInputActive, setIsInputActive] = useState(false);
    const [tempRating, setTempRating] = useState(0);
    const [showConfirmModal, setShowConfirmModal] = useState(false);
    const gaugeRef = useRef(null);
    const [isDarkMode, setIsDarkMode] = useState(true);

    // Detect theme by checking if any parent has light-mode class
    useEffect(() => {
        const checkTheme = () => {
            const isLightMode = document.querySelector('.light-mode') !== null;
            setIsDarkMode(!isLightMode);
        };

        checkTheme();
        
        // Watch for theme changes by using a MutationObserver
        const observer = new MutationObserver((mutations) => {
            mutations.forEach((mutation) => {
                if (mutation.attributeName === 'class') {
                    checkTheme();
                }
            });
        });

        // Start observing the document body for class changes
        observer.observe(document.body, { attributes: true, childList: true, subtree: true });

        return () => observer.disconnect();
    }, []);

    // Add click outside listener
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (gaugeRef.current && !gaugeRef.current.contains(event.target)) {
                setIsInputActive(false);
                setTempRating(0);
            }
        };

        if (isInputActive) {
            document.addEventListener('click', handleClickOutside);
        }

        return () => {
            document.removeEventListener('click', handleClickOutside);
        };
    }, [isInputActive]);

    // Calculate rating based on interaction
    const calculateRatingFromClick = (e) => {
        if (!gaugeRef.current) return 0;
        
        if (compact) {
            // For circular gauge, calculate based on angle of click
            const rect = gaugeRef.current.getBoundingClientRect();
            const centerX = rect.left + rect.width / 2;
            const centerY = rect.top + rect.height / 2;
            
            // Calculate angle
            const angle = Math.atan2(e.clientY - centerY, e.clientX - centerX);
            
            // Convert angle to percentage (0 to 100)
            // atan2 returns angle in radians (-π to π)
            // We convert to 0-360 degrees, then to percentage
            let degrees = (angle * 180 / Math.PI + 360) % 360;
            
            // Adjust so 0 degrees (right) is 0%, 360 degrees is 100%
            if (degrees < 270) degrees += 90;
            else degrees = degrees - 270;
            
            const percentage = Math.round((degrees / 360) * 100);
            return Math.min(100, Math.max(0, percentage));
        } else {
            // For horizontal bar, calculate based on X position
            const rect = gaugeRef.current.getBoundingClientRect();
            const x = e.clientX - rect.left;
            const width = rect.width;
            
            // Calculate percentage based on position
            let value = Math.round((x / width) * 100);
            return Math.max(0, Math.min(100, value));
        }
    };

    const handleGaugeClick = (e) => {
        if (isStatic) return; // Disable interaction for static gauges
        
        e.stopPropagation();
        
        if (!isInputActive) {
            setIsInputActive(true);
            const initialValue = calculateRatingFromClick(e);
            setTempRating(initialValue);
        } else {
            if (tempRating > 0) {
                setShowConfirmModal(true);
            } else {
                setIsInputActive(false);
                setTempRating(0);
            }
        }
    };

    const handleMouseMove = (e) => {
        if (isStatic || !isInputActive) return;
        const value = calculateRatingFromClick(e);
        setTempRating(value);
    };

    const handleConfirm = () => {
        onRatingSubmit(tempRating);
        setIsInputActive(false);
        setTempRating(0);
        setShowConfirmModal(false);
    };

    const handleCancel = () => {
        setShowConfirmModal(false);
    };

    // Active value to display
    const displayValue = isInputActive ? tempRating : Math.round(rating.average || 0);
    
    // For standard horizontal bar gauge
    if (!compact) {
        // Colors for different themes
        const backgroundBarColor = isDarkMode ? "rgba(255, 255, 255, 0.1)" : "rgba(0, 0, 0, 0.05)";
        const activeRatingColor = "#4CAF50";
        const displayWidth = `${displayValue}%`;

        return (
            <>
                <div 
                    ref={gaugeRef}
                    className={`gauge ${isInputActive ? 'active' : ''}`}
                    onClick={handleGaugeClick}
                    onMouseMove={handleMouseMove}
                    style={{ 
                        height: '40px',
                        padding: '15px 0',
                        position: 'relative' 
                    }}
                >
                    {/* For accessibility, we keep the svg but don't display it */}
                    <svg viewBox="0 0 100 10" style={{ display: 'none' }}>
                        <rect width="100" height="10" />
                    </svg>
                    
                    {/* Horizontal bar background */}
                    <div
                        style={{
                            position: 'absolute',
                            top: '15px',
                            left: '0',
                            width: '100%',
                            height: '8px',
                            backgroundColor: backgroundBarColor,
                            borderRadius: '4px'
                        }}
                    ></div>
                    
                    {/* Rating indicator */}
                    <div
                        style={{
                            position: 'absolute',
                            top: '15px',
                            left: '0',
                            width: displayWidth,
                            height: '8px',
                            backgroundColor: activeRatingColor,
                            borderRadius: '4px',
                            transition: isInputActive ? 'none' : 'width 0.3s ease'
                        }}
                    ></div>
                    
                    <div className="gauge-label">
                        <p style={{ margin: '20px 0 0' }}>{displayValue}%</p>
                        <small style={{ 
                            color: isDarkMode ? 'rgba(255,255,255,0.5)' : 'rgba(0,0,0,0.5)',
                            fontSize: '0.7em',
                            marginTop: '5px'
                        }}>
                            {rating.count} {rating.count === 1 ? 'rating' : 'ratings'}
                        </small>
                    </div>
                </div>

                {/* Confirmation Modal */}
                {showConfirmModal && (
                    <div className="modal-overlay">
                        <div className="rating-confirm-modal">
                            <h3>Confirm Rating</h3>
                            <p>Submit rating of {tempRating}%?</p>
                            <div className="modal-buttons">
                                <button 
                                    className="modal-button confirm"
                                    onClick={handleConfirm}
                                >
                                    Confirm
                                </button>
                                <button 
                                    className="modal-button cancel"
                                    onClick={handleCancel}
                                >
                                    Cancel
                                </button>
                            </div>
                        </div>
                    </div>
                )}
            </>
        );
    }
    
    // For compact circular gauge
    return (
        <>
            <div 
                ref={gaugeRef}
                className={`gauge compact ${isInputActive ? 'active' : ''}`}
                onClick={handleGaugeClick}
                onMouseMove={handleMouseMove}
            >
                {/* For accessibility, we keep the svg but don't display it */}
                <svg viewBox="0 0 100 100" style={{ display: 'none' }}>
                    <circle cx="50" cy="50" r="45" />
                </svg>
                
                {/* Remove icon rendering */}
                
                {/* Circular gauge visualization */}
                <div 
                    className="rating-circle" 
                    style={{
                        '--gauge-value': `${displayValue}%`
                    }}
                >
                    <span className="rating-value" style={{ fontSize: '0.9em' }}>{displayValue}%</span>
                </div>
                
                {/* Rating count - only show for non-static gauges */}
                {!isStatic && (
                    <div className="rating-count">
                        {rating.count} {rating.count === 1 ? 'rating' : 'ratings'}
                    </div>
                )}
            </div>

            {/* Confirmation Modal */}
            {showConfirmModal && (
                <div className="modal-overlay">
                    <div className="rating-confirm-modal">
                        <h3>Confirm Rating</h3>
                        <p>Submit rating of {tempRating}%?</p>
                        <div className="modal-buttons">
                            <button 
                                className="modal-button confirm"
                                onClick={handleConfirm}
                            >
                                Confirm
                            </button>
                            <button 
                                className="modal-button cancel"
                                onClick={handleCancel}
                            >
                                Cancel
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
};